import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, BarChart3, BookOpen, Clock, Target, TrendingUp,
  Play, CheckCircle, AlertCircle, ArrowRight, Calendar, Award,
  Brain, Stethoscope, Heart, Activity, Users, FileText, LogOut
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EunacomDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentSimulations, setRecentSimulations] = useState([]);
  const [specialtyProgress, setSpecialtyProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('eunacom_user');
    if (!storedUser) {
      navigate('/eunacom');
      return;
    }
    setUser(JSON.parse(storedUser));
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('eunacom_token');
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [subRes, statsRes, simsRes, progressRes] = await Promise.all([
        fetch(`${API_URL}/api/eunacom/my-subscription`, { headers }),
        fetch(`${API_URL}/api/eunacom/my-stats`, { headers }),
        fetch(`${API_URL}/api/eunacom/my-simulations?limit=5`, { headers }),
        fetch(`${API_URL}/api/eunacom/my-progress-by-specialty`, { headers })
      ]);

      if (subRes.ok) {
        const data = await subRes.json();
        setSubscription(data.subscription);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (simsRes.ok) {
        const data = await simsRes.json();
        setRecentSimulations(data.simulations || []);
      }
      if (progressRes.ok) {
        const data = await progressRes.json();
        setSpecialtyProgress(data.specialties || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('eunacom_user');
    localStorage.removeItem('eunacom_token');
    navigate('/eunacom');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    if (!subscription?.end_date) return 0;
    const endDate = new Date(subscription.end_date);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const simulacionesDisponibles = subscription?.simulaciones_disponibles || 0;
  const simulacionesUsadas = subscription?.simulaciones_usadas || 0;
  const porcentajeUso = simulacionesDisponibles > 0 
    ? Math.round((simulacionesUsadas / (simulacionesDisponibles + simulacionesUsadas)) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">EUNACOM</h1>
              <p className="text-xs text-slate-400">Preparación Individual</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-300 text-sm">Hola, {user?.nombre || 'Usuario'}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Subscription Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border-emerald-500/30">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">
                    {subscription?.plan_nombre || 'EUNACOM Individual'}
                  </h2>
                  <p className="text-slate-300">
                    Estado: <span className={`font-medium ${subscription?.status === 'activa' ? 'text-emerald-400' : 'text-yellow-400'}`}>
                      {subscription?.status === 'activa' ? 'Activa' : 'Pendiente'}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{simulacionesDisponibles}</p>
                    <p className="text-xs text-slate-400">Simulaciones disponibles</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-400">{getDaysRemaining()}</p>
                    <p className="text-xs text-slate-400">Días restantes</p>
                  </div>
                </div>
              </div>
              {subscription?.end_date && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Vigencia hasta: {formatDate(subscription.end_date)}</span>
                    <Button 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-500"
                      onClick={() => navigate('/eunacom/packs')}
                    >
                      Comprar más simulaciones
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <Play className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats?.total_simulaciones || 0}</p>
                <p className="text-xs text-slate-400">Simulaciones realizadas</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <Target className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats?.promedio_score || 0}%</p>
                <p className="text-xs text-slate-400">Promedio general</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats?.mejor_score || 0}%</p>
                <p className="text-xs text-slate-400">Mejor puntuación</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats?.tiempo_total_horas || 0}h</p>
                <p className="text-xs text-slate-400">Tiempo de práctica</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Actions */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Play className="w-5 h-5 text-emerald-400" />
                    Iniciar Práctica
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500"
                    onClick={() => navigate('/eunacom/simulacion/nueva')}
                    disabled={simulacionesDisponibles === 0}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Nueva Simulación
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                    onClick={() => navigate('/eunacom/casos')}
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Banco de Casos
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                    onClick={() => navigate('/eunacom/evaluacion')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Evaluación Integral
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Quick Stats */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Tu Progreso
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Uso de simulaciones</span>
                        <span className="text-white">{porcentajeUso}%</span>
                      </div>
                      <Progress value={porcentajeUso} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Promedio vs Meta (70%)</span>
                        <span className="text-white">{stats?.promedio_score || 0}%</span>
                      </div>
                      <Progress 
                        value={Math.min(100, ((stats?.promedio_score || 0) / 70) * 100)} 
                        className="h-2" 
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Center Column - Recent Activity */}
          <div className="lg:col-span-1">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <Card className="bg-slate-800/50 border-slate-700 h-full">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-400" />
                    Actividad Reciente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentSimulations.length === 0 ? (
                    <div className="text-center py-8">
                      <Brain className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">Aún no has realizado simulaciones</p>
                      <p className="text-slate-500 text-sm">¡Comienza tu primera práctica!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentSimulations.map((sim, idx) => (
                        <div 
                          key={sim.id || idx}
                          className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer"
                          onClick={() => navigate(`/eunacom/simulacion/${sim.id}/resultado`)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm font-medium">{sim.caso_nombre || 'Caso clínico'}</p>
                              <p className="text-slate-400 text-xs">{sim.especialidad || 'General'}</p>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${sim.score >= 70 ? 'text-emerald-400' : 'text-orange-400'}`}>
                                {sim.score || 0}%
                              </p>
                              <p className="text-slate-500 text-xs">{formatDate(sim.fecha)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right Column - Specialties Progress */}
          <div className="lg:col-span-1">
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
              <Card className="bg-slate-800/50 border-slate-700 h-full">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Stethoscope className="w-5 h-5 text-cyan-400" />
                    Progreso por Especialidad
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {specialtyProgress.length === 0 ? (
                    <div className="text-center py-8">
                      <Award className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">Sin datos aún</p>
                      <p className="text-slate-500 text-sm">Realiza simulaciones para ver tu progreso</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {specialtyProgress.slice(0, 5).map((spec, idx) => (
                        <div key={spec.codigo || idx}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-300">{spec.nombre}</span>
                            <span className={`font-medium ${spec.promedio >= 70 ? 'text-emerald-400' : 'text-orange-400'}`}>
                              {spec.promedio}%
                            </span>
                          </div>
                          <Progress 
                            value={spec.promedio} 
                            className="h-2" 
                          />
                          <p className="text-xs text-slate-500 mt-1">{spec.casos_realizados} casos realizados</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-8"
        >
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-yellow-400" />
                Recomendaciones de Estudio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertCircle className="w-5 h-5 text-orange-400" />
                    <span className="text-white font-medium">Áreas a reforzar</span>
                  </div>
                  <p className="text-slate-400 text-sm">
                    {stats?.areas_debiles?.length > 0 
                      ? stats.areas_debiles.join(', ')
                      : 'Realiza más simulaciones para identificar áreas de mejora'}
                  </p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-white font-medium">Fortalezas</span>
                  </div>
                  <p className="text-slate-400 text-sm">
                    {stats?.fortalezas?.length > 0 
                      ? stats.fortalezas.join(', ')
                      : 'Continúa practicando para identificar tus fortalezas'}
                  </p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <span className="text-white font-medium">Próximo objetivo</span>
                  </div>
                  <p className="text-slate-400 text-sm">
                    {stats?.proximo_objetivo || 'Completa 5 simulaciones esta semana'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
